
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { FOUR_GROUP_TRAIT_GROUPINGS, Trait } from "@/lib/consts";
import { getOrientationText, getTraitContent, OrientationTextResult } from "@/lib/elraReport7DynamicText";
import { CompetencyStat, PCT_BAND_COLORS } from "./shared";

// Note: "poeple-orientation" key matches the existing typo in FOUR_GROUP_TRAIT_GROUPINGS.
const ORIENTATION_LETTERS: Record<string, string> = {
  "strategic-orientation": "S",
  "poeple-orientation": "P",
  "outcome-orientation": "O",
  "communication-orientation": "C",
};

interface OrientationGroupView {
  id: string;
  letter: string;
  label: string;
  description: string;
  stats: CompetencyStat[];
  text: OrientationTextResult;
}

@Component
export default class TeamOrientations extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true }) readonly stats!: CompetencyStat[];
  @Prop({ default: false }) readonly printing!: boolean;

  public openIds: string[] = [];
  public expandedTraits: Record<string, boolean> = {};

  get bands() {
    return (["high", "good", "mixed", "low"] as const).map((key) => ({ key, color: PCT_BAND_COLORS[key] }));
  }

  // Team-level `{trait}_status` record derived from % prioritizing, so the orientation
  // narrative generator can be reused as-is.
  get teamStatusScoring(): Record<string, number> {
    const out: Record<string, number> = {};
    this.stats.forEach((s) => (out[`${s.trait}_status`] = s.teamStatus));
    return out;
  }

  get orientationGroups(): OrientationGroupView[] {
    return Object.entries(FOUR_GROUP_TRAIT_GROUPINGS).map(([id, traits]) => {
      const stats = (traits as Trait[]).map((t) => this.stats.find((s) => s.trait === t)).filter((s): s is CompetencyStat => !!s);
      return {
        id,
        letter: ORIENTATION_LETTERS[id] ?? "",
        label: this.$t(`Orientation::${id}::Label`).toString(),
        description: this.$t(`Orientation::${id}::Description`).toString(),
        stats,
        text: getOrientationText(id, this.teamStatusScoring, this.$i18n),
      };
    });
  }

  get openGroups(): OrientationGroupView[] {
    if (this.printing) return this.orientationGroups;
    return this.orientationGroups.filter((og) => this.openIds.includes(og.id));
  }

  isOpen(id: string): boolean {
    return this.printing || this.openIds.includes(id);
  }

  onOrientClick(id: string, e: MouseEvent): void {
    if (e.shiftKey) {
      const next = this.openIds.includes(id) ? this.openIds.filter((x) => x !== id) : [...this.openIds, id];
      this.openIds = next.length === 0 ? [id] : next;
    } else {
      this.openIds = this.openIds.length === 1 && this.openIds[0] === id ? [] : [id];
    }
  }

  closeOrient(id: string): void {
    this.openIds = this.openIds.filter((x) => x !== id);
  }

  toggleTrait(trait: string): void {
    this.$set(this.expandedTraits, trait, !this.expandedTraits[trait]);
  }

  traitContent(trait: string): { definition: string; behaviours: string[] } {
    return getTraitContent(trait as Trait, this.$i18n);
  }

  mounted() {
    const first = this.orientationGroups[0]?.id;
    if (first) this.openIds = [first];
  }
}
